import { Chess } from 'chess.js';
import type { Color, Key } from 'chessground/types';
import type { PracticeColor, PracticeResult } from './types';

/**
 * State machine immutable de la partie Practice Arena (T4 P1).
 *
 * Discriminated union sur `kind` :
 *  - `idle`             — avant START_GAME (page en cours de loading state initial)
 *  - `your_turn`        — board interactif, Léon doit jouer
 *  - `engine_thinking`  — Léon a joué, on attend le coup engine via playMove()
 *  - `game_over`        — partie terminée (résultat connu, abandonné, ou mat)
 *
 * Source unique de vérité : `fen` (string). Toute Chess instance est
 * reconstruite localement à chaque transition — pas d'instance mutable
 * partagée (cf. retex bug A2 MVP2.5 sur Tactics).
 *
 * L'historique des coups joués est dans `history` (liste de PracticePlyEntry)
 * pour replay/affichage. Le numéro de ply courant = `history.length`.
 */

export interface PracticePlyEntry {
  ply: number;
  san: string;
  uci: string;
  /** Délai entre 'your_turn' (ou debut) et le dispatch de MOVE_PLAYER/MOVE_ENGINE en ms. */
  timeSpentMs: number;
  /** 'player' = coup Léon, 'engine' = coup Stockfish. */
  by: 'player' | 'engine';
}

export interface PracticeGameSetup {
  gameId: number;
  levelElo: number;
  /** Couleur des pièces de Léon (résolu si 'random' avant START_GAME). */
  playerColor: Color;
  startingFen: string;
  /** Performance.now() de démarrage de partie — référence pour timeSpentMs. */
  startedAtMs: number;
}

export type PracticeGameState =
  | { kind: 'idle' }
  | {
      kind: 'your_turn';
      setup: PracticeGameSetup;
      fen: string;
      history: PracticePlyEntry[];
      lastMove: [Key, Key] | undefined;
      /** Performance.now() du dernier dispatch — pour calculer timeSpentMs du prochain coup. */
      turnStartedAtMs: number;
    }
  | {
      kind: 'engine_thinking';
      setup: PracticeGameSetup;
      fen: string;
      history: PracticePlyEntry[];
      /**
       * `undefined` quand l'engine ouvre la partie (Léon noir, FEN standard) :
       * pas de coup précédent à highlighter. Sinon `[from, to]` du coup Léon.
       */
      lastMove: [Key, Key] | undefined;
      turnStartedAtMs: number;
    }
  | {
      kind: 'game_over';
      setup: PracticeGameSetup;
      fen: string;
      history: PracticePlyEntry[];
      lastMove: [Key, Key] | undefined;
      result: PracticeResult;
      /** Performance.now() à la fin de partie — pour durée totale. */
      endedAtMs: number;
    };

export type PracticeGameAction =
  | { type: 'START_GAME'; setup: PracticeGameSetup; now: number }
  | {
      type: 'MOVE_PLAYER';
      from: Key;
      to: Key;
      promotion?: 'q' | 'r' | 'b' | 'n';
      now: number;
    }
  | {
      type: 'MOVE_ENGINE';
      uci: string;
      now: number;
    }
  | { type: 'ABANDON'; now: number }
  | { type: 'GAME_OVER_DETECTED'; result: PracticeResult; now: number };

/**
 * Résout la couleur de jeu de Léon à partir du paramètre `practice_color`.
 * Pour 'random', alterne pseudo-aléatoirement (Math.random au call-site).
 */
export function resolvePlayerColor(color: PracticeColor, randomSeed?: number): Color {
  if (color === 'white') return 'white';
  if (color === 'black') return 'black';
  const seed = randomSeed ?? Math.random();
  return seed < 0.5 ? 'white' : 'black';
}

/**
 * Détecte si la position est terminée (mat, pat, 50-coups, répétition,
 * matériel insuffisant). Retourne le résultat ou null si la partie continue.
 *
 * IMPORTANT : `new Chess(fen)` perd l'historique des coups → la 3-fold
 * repetition et la règle des 50 coups (`isThreefoldRepetition` / `isDraw`
 * basé sur halfmove clock + repetition) ne se détecteraient pas
 * correctement à partir du FEN seul. On replay donc l'historique UCI
 * depuis le FEN initial pour reconstruire un état chess.js complet.
 *
 * Convention : `playerColor` = couleur de Léon. Si chess.turn() = playerColor
 * ET la position est mat → Léon a perdu (engine a maté Léon). Inversement.
 */
export function detectEndOfGame(
  startingFen: string,
  historyUcis: readonly string[],
  playerColor: Color,
): PracticeResult | null {
  const c = new Chess(startingFen);
  for (const uci of historyUcis) {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promo = uci.length >= 5 ? uci[4] : undefined;
    try {
      c.move({ from, to, ...(promo ? { promotion: promo } : {}) });
    } catch {
      // Historique corrompu — on s'arrête au dernier coup valide. Mieux
      // que d'exploser : permet au moins de détecter le mat actuel.
      break;
    }
  }
  if (c.isCheckmate()) {
    // c.turn() = côté qui DEVRAIT jouer mais ne peut pas → côté maté.
    return c.turn() === playerColor[0] ? 'loss' : 'win';
  }
  if (c.isStalemate() || c.isInsufficientMaterial() || c.isThreefoldRepetition() || c.isDraw()) {
    return 'draw';
  }
  return null;
}

export function practiceGameReducer(
  state: PracticeGameState,
  action: PracticeGameAction,
): PracticeGameState {
  switch (action.type) {
    case 'START_GAME': {
      // À START_GAME, on regarde qui doit jouer selon le FEN initial.
      // Si c'est Léon (playerColor === side-to-move) → your_turn.
      // Sinon → engine_thinking (l'engine doit ouvrir, ex. Léon = noir sur FEN standard).
      const chess = new Chess(action.setup.startingFen);
      const sideToMove: Color = chess.turn() === 'w' ? 'white' : 'black';
      const isPlayerTurn = sideToMove === action.setup.playerColor;
      if (isPlayerTurn) {
        return {
          kind: 'your_turn',
          setup: action.setup,
          fen: action.setup.startingFen,
          history: [],
          lastMove: undefined,
          turnStartedAtMs: action.now,
        };
      }
      return {
        kind: 'engine_thinking',
        setup: action.setup,
        fen: action.setup.startingFen,
        history: [],
        // Pas de coup précédent réel : l'engine ouvre. `undefined` évite que
        // chessground highlight une case sentinel (bug visuel M5).
        lastMove: undefined,
        turnStartedAtMs: action.now,
      };
    }

    case 'MOVE_PLAYER': {
      if (state.kind !== 'your_turn') return state;
      const chess = new Chess(state.fen);
      let move;
      try {
        move = chess.move({
          from: action.from,
          to: action.to,
          ...(action.promotion ? { promotion: action.promotion } : {}),
        });
      } catch {
        // Coup illégal — board UI affiche pas, mais on protège quand même.
        return state;
      }
      if (!move) return state;

      const ply = state.history.length + 1;
      const newHistory: PracticePlyEntry[] = [
        ...state.history,
        {
          ply,
          san: move.san,
          uci: move.from + move.to + (move.promotion ?? ''),
          timeSpentMs: Math.max(0, action.now - state.turnStartedAtMs),
          by: 'player',
        },
      ];

      const newFen = chess.fen();
      const endResult = detectEndOfGame(
        state.setup.startingFen,
        newHistory.map((e) => e.uci),
        state.setup.playerColor,
      );
      if (endResult !== null) {
        return {
          kind: 'game_over',
          setup: state.setup,
          fen: newFen,
          history: newHistory,
          lastMove: [action.from, action.to],
          result: endResult,
          endedAtMs: action.now,
        };
      }

      return {
        kind: 'engine_thinking',
        setup: state.setup,
        fen: newFen,
        history: newHistory,
        lastMove: [action.from, action.to],
        turnStartedAtMs: action.now,
      };
    }

    case 'MOVE_ENGINE': {
      if (state.kind !== 'engine_thinking') return state;
      const chess = new Chess(state.fen);
      const from = action.uci.slice(0, 2) as Key;
      const to = action.uci.slice(2, 4) as Key;
      const promo = action.uci.length >= 5 ? (action.uci[4] as 'q' | 'r' | 'b' | 'n') : undefined;
      let move;
      try {
        move = chess.move({ from, to, ...(promo ? { promotion: promo } : {}) });
      } catch {
        move = null;
      }
      if (!move) {
        // Engine a renvoyé un coup illégal (rarissime — bug Stockfish, mauvais
        // FEN, ou worker corrompu). On force la partie en nulle (convention :
        // engine bug = draw) pour débloquer Léon plutôt que rester coincé en
        // engine_thinking. Bug H4 (salve P2).
        console.error(
          `[practice-arena] engine returned illegal UCI: ${action.uci} for FEN ${state.fen}`,
        );
        return {
          kind: 'game_over',
          setup: state.setup,
          fen: state.fen,
          history: state.history,
          lastMove: state.lastMove,
          result: 'draw',
          endedAtMs: action.now,
        };
      }

      const ply = state.history.length + 1;
      const newHistory: PracticePlyEntry[] = [
        ...state.history,
        {
          ply,
          san: move.san,
          uci: action.uci,
          timeSpentMs: Math.max(0, action.now - state.turnStartedAtMs),
          by: 'engine',
        },
      ];

      const newFen = chess.fen();
      const endResult = detectEndOfGame(
        state.setup.startingFen,
        newHistory.map((e) => e.uci),
        state.setup.playerColor,
      );
      if (endResult !== null) {
        return {
          kind: 'game_over',
          setup: state.setup,
          fen: newFen,
          history: newHistory,
          lastMove: [from, to],
          result: endResult,
          endedAtMs: action.now,
        };
      }

      return {
        kind: 'your_turn',
        setup: state.setup,
        fen: newFen,
        history: newHistory,
        lastMove: [from, to],
        turnStartedAtMs: action.now,
      };
    }

    case 'ABANDON': {
      if (state.kind === 'idle' || state.kind === 'game_over') return state;
      return {
        kind: 'game_over',
        setup: state.setup,
        fen: state.fen,
        history: state.history,
        lastMove: state.lastMove,
        result: 'abandoned',
        endedAtMs: action.now,
      };
    }

    case 'GAME_OVER_DETECTED': {
      if (state.kind === 'idle' || state.kind === 'game_over') return state;
      return {
        kind: 'game_over',
        setup: state.setup,
        fen: state.fen,
        history: state.history,
        lastMove: state.lastMove,
        result: action.result,
        endedAtMs: action.now,
      };
    }

    default:
      return state;
  }
}

/**
 * Calcule le PGN à partir de la state finale. Utilisé au moment du finish().
 * Implémentation minimale : reconstruit via chess.js depuis fen initial +
 * replay des coups par UCI.
 */
export function buildPgn(state: PracticeGameState): string {
  if (state.kind === 'idle') return '';
  const chess = new Chess(state.setup.startingFen);
  for (const entry of state.history) {
    const from = entry.uci.slice(0, 2);
    const to = entry.uci.slice(2, 4);
    const promo = entry.uci.length >= 5 ? entry.uci[4] : undefined;
    try {
      chess.move({ from, to, ...(promo ? { promotion: promo } : {}) });
    } catch {
      break;
    }
  }
  return chess.pgn();
}
